<template>
	<div>
		<!-- Submenu -->
		<claim-detail-submenu />

		<core-layout-content large>
			<div class="xs-12">
				<!-- Particulars -->
				<claim-particulars :claim="computedClaim" @updated="fetchClaim" />

				<!-- Notes -->
				<core-notes-card
					class="mt6"
					notesOf="claim"
					:id="this.id"
					:list="computedNotes"
					:canWrite="canEditApprovedClaims(computedClaim.status)"
					@note:created="fetchClaim"
					@note:updated="fetchClaim"
					@note:deleted="fetchClaim"
				/>

				<!-- Summary -->
				<claim-summary :data="claimSummary" class="mt6" />

				<!-- Benefits to claim -->
				<claim-benefits
					class="mt2"
					:claimId="$route.params.id"
					:policyId="computedPolicyId"
					:benefitClaims="benefitClaims"
					:claimStatus="computedClaim.status"
					@deleted="fetchClaimSummary(), fetchClaimBenefitsAndBalance()"
					@updated="fetchClaimSummary(), fetchClaimBenefitsAndBalance()"
					@added="fetchClaimSummary(), fetchClaimBenefitsAndBalance()"
				/>

				<!-- Claim Balance - tree of benefits with remaining values -->
				<claim-balance
					class="mt6"
					:claimBalance="claimBalance"
					:benefitGraph="benefitGraph"
				/>

				<!-- Facts -->
				<aeg-claim-facts
					v-if="
						$store.state.tenantId === 'aeg_uat' ||
							$store.state.tenantId === 'aeg'
					"
					class="mt6"
					:id="id"
					:facts="computedFacts"
				/>
				<claim-facts
					v-else
					class="mt6"
					:data="computedFacts"
					:claimId="computedClaim.id"
					:policyId="computedPolicyId"
					:claimStatus="computedClaim.status"
					@updated="fetchClaim"
					@deleted="fetchClaim"
					@added="fetchClaim"
				/>

				<!-- Codes Info -->
				<codes-info class="mt6" :claim="computedClaim" @updated="fetchClaim" />

				<!-- Info -->
				<core-info-card
					class="mt6"
					:data="computedClaim"
					:hasReferral="false"
					:hasStatus="false"
				/>

				<!-- Rejection Info -->
				<claim-rejection-info
					v-if="computedClaim.status === 'REJECTED'"
					class="mt6"
					:rejectionCodes="computedRejectionCodes"
					:rejectionRemarks="computedRejectionRemarks"
				/>

				<!-- Files -->
				<core-files-card
					class="mt6"
					filesOf="claim"
					:id="this.id"
					:list="claimFiles"
					:canWrite="canEditApprovedClaims(computedClaim.status)"
					@file:deleted="fetchFiles"
					@files:uploaded="fetchFiles"
				/>

				<!-- Stakeholders -->
				<!-- MOCK: mocking CMS component here -->
				<!-- Should be refactored and the whole details should come from CMS -->
				<aeg-claim-stakeholders
					class="mt6"
					v-if="
						$store.state.tenantId === 'aeg_uat' ||
							$store.state.tenantId === 'aeg'
					"
				/>
			</div>

			<!-- Footer menu -->
			<div
				v-if="canEditApprovedClaims(computedClaim.status)"
				class="flex wrap xs-12 xs-between bt1 bc7 pt2 mt8"
				v-can="'writeClaims'"
			>
				<j-button
					type="primary"
					:isLoading="isLoading"
					@click="$modal.show('downloadPdf')"
					>{{ $t("download_pdf") }}</j-button
				>
				<div class="flex xs-6 xs-right">
					<j-button
						class="mr2"
						type="secondary"
						@click="$modal.show('rejectClaimModal')"
						:isLoading="isLoading"
						>{{ $t("reject_claim") }}</j-button
					>
					<j-button type="error" @click="deleteClaim" :isLoading="isLoading">
						<crm-icon name="delete" size="normal" color="c5" class="mr1" />
						{{ $t("delete") }}
					</j-button>
				</div>
			</div>
		</core-layout-content>

		<!-- Rejection Modal -->
		<core-modal name="rejectClaimModal" :headline="$t('reject_claim')">
			<claim-rejection
				:policyId="computedPolicyId"
				:codes="computedRejectionCodes"
				:remarks="computedRejectionRemarks"
				@rejected="afterRejectClaim"
			/>
		</core-modal>

		<!-- Download pdf -->
		<core-modal name="downloadPdf" :headline="$t('download_pdf')">
			<div class="p4">
				<!-- Select template -->
				<div class="fw7 pt2 pb2">Choose a PDF template</div>
				<j-base-multi-select-with-flexible-value
					v-model="selectedTemplate"
					:options="templates"
					:allowMultiselect="false"
					:allowLocalSearch="true"
					placeholder="Search template..."
					class="mb15"
				/>
				<!-- <core-graphql-variables-builder
						:graphQlQuery="graphQlQuery"
						:values="getValues()"
						@update:variables="graphQlVariables = $event"
						class="mb3"
				/>-->
				<j-button
					class="mt15"
					size="small"
					@click="downloadPdf"
					:isLoading="isLoading"
					>{{ $t("download_pdf") }}</j-button
				>
			</div>
		</core-modal>

		<!-- Loading toast -->
		<j-loading-toast v-if="isLoading" />
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import CrmIcon from "@/components/CrmIcon"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreInfoCard from "@/modules/core/components/CoreInfoCard"
import CoreNotesCard from "@/modules/core/components/CoreNotesCard/index.vue"
import CoreFilesCard from "@/modules/core/components/CoreFilesCard"
import ClaimDetailSubmenu from "../components/ClaimDetailSubmenu"
import ClaimParticulars from "../components/ClaimParticulars"
import ClaimBenefits from "../components/ClaimBenefits"
import ClaimFacts from "../components/ClaimFacts"
import ClaimRejectionInfo from "../components/ClaimRejectionInfo"
import JLoadingToast from "@/components/JLoadingToast"
import CoreModal from "@/modules/core/components/CoreModal"
import ClaimRejection from "../components/ClaimRejection"
import CodesInfo from "../components/CodesInfo"
import ClaimSummary from "../components/ClaimSummary"
import ClaimBalance from "../components/ClaimBalance"
import dictionary from "../components/BenefitValuesTemplates/dictionary"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"
import { canEditApprovedClaims } from "../utils/"

// MOCK: CMS
import AegClaimStakeholders from "../components/mockedCms/AegClaimStakeholders"
import AegClaimFacts from "../components/mockedCms/AegClaimFacts"

import api from "../api/"
import get from "lodash.get"

export default {
	components: {
		ClaimBenefits,
		ClaimFacts,
		ClaimParticulars,
		ClaimDetailSubmenu,
		ClaimRejectionInfo,
		JCard,
		JButton,
		CrmIcon,
		CoreLayoutContent,
		CoreInfoCard,
		CoreNotesCard,
		CoreFilesCard,
		JLoadingToast,
		CoreModal,
		ClaimRejection,
		CodesInfo,
		ClaimSummary,
		ClaimBalance,
		AegClaimStakeholders,
		AegClaimFacts,
		JBaseMultiSelectWithFlexibleValue
	},
	props: {
		id: { type: String, required: true }
	},
	data() {
		return {
			isLoading: false,
			templates: null,
			selectedTemplate: null,
			claim: null, // from api - info widget, etc.
			claimSummary: {}, // from api - just a small call to get claim summary
			benefitClaims: [], // from api - benefitClaims merged witb claimBalance fields
			claimBalance: [], // from api - claimBalance
			benefitGraph: [], // from api - claims.policy.acceptedOffer.product.benefitGrahp
			claimFiles: [] // from api - files for claims
		}
	},

	computed: {
		computedClaim() {
			return this.claim && this.claim[0] ? this.claim[0] : {}
		},
		computedFacts() {
			return this?.claim?.[0]?.facts || []
		},
		computedPolicyId() {
			return this.computedClaim?.policy?.id
		},
		computedRejectionCodes() {
			return this?.claim?.[0]?.rejectionCodes || []
		},
		computedRejectionRemarks() {
			return this.computedClaim?.rejectionRemarks
		},
		computedNotes() {
			return this?.claim?.[0]?.notes
		}
	},

	methods: {
		async fetchClaim() {
			this.isLoading = true
			const res = await api.claim({ where: { id: this.id } })
			this.isLoading = false
			this.claim = get(res, "data.claims.list")
		},

		async fetchClaimSummary() {
			const res = await api.claimSummary({ where: { id: this.id } })
			this.claimSummary = res?.data?.claims?.list?.[0]?.totalValues || {}
		},

		async deleteClaim() {
			this.isLoading = true
			const res = await api.deleteClaim({ claimId: this.id })

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				this.isLoading = false
				return
			}

			// Succes
			this.$router.push({ name: "ClaimsList" })
			this.isLoading = false
		},

		async downloadPdf() {
			let apiKey =
				this.selectedTemplate?.[0]?.typeName === "pdfDrawingTemplate"
					? "renderPdfDrawingTemplate"
					: "renderWkhtmltopdfTemplate"

			let query =
				this.selectedTemplate?.[0]?.typeName === "pdfDrawingTemplate"
					? "pdfDrawingQuery"
					: "wkhtmltopdfQuery"

			let variables = {
				templateId: this.selectedTemplate?.[0]?.value,
				input: {
					name: "data",
					variablesJsonString: JSON.stringify({
						claimWhere1: { id: this.id }
					})
				}
			}

			this.isLoading = true
			const res = await api[query](variables)
			this.isLoading = false

			if (res?.data?.[apiKey]?.errors && res?.data?.[apiKey]?.errors?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.errors?.toString() || res.data?.[apiKey]?.errors
					}
				})
			}

			if (res.data?.[apiKey]?.status === "success") {
				let bytes = new Uint8Array(res.data?.[apiKey]?.value) // pass your byte response to this constructor
				let blob = new Blob([bytes], { type: "application/pdf" }) // change resultByte to bytes
				let link = document.createElement("a")
				link.href = window.URL.createObjectURL(blob)
				link.download = "test.pdf"
				link.click()
			}

			this.$modal.hide("downloadPdf")
		},

		/**
		 * This method is fetching claims.benefitClaims and claims.claimBalance in one query
		 * We then combine claims.benefitClaims and claims.claimBalance together run validation on it and save it as benefitClaims
		 * We also run validation on claims.claimBalance and save it as claimBalance
		 */
		async fetchClaimBenefitsAndBalance() {
			const res = await api.benefitClaimsAndBalance({ where: { id: this.id } })

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			const claim = res?.data?.claims?.list?.[0]
			if (!claim) return

			// Assign claimBalance separately as we use it in ClaimBalance component
			this.claimBalance = claim?.claimBalance?.map(item => ({
				...item,
				isValid: this.validateClaimBenefit(item.value, item.typeId)
			}))

			// Append remainig value from claimBalance to each particular benefit
			this.benefitClaims = claim?.benefitClaims?.map(item => {
				const remainingScalarValue = claim?.claimBalance?.find(
					balance => balance.typeId === item?.benefit?.typeId
				)?.value

				const remainingValue = claim?.claimBalance?.find(
					balance => balance.typeId === item?.benefit?.typeId
				)?.formattedValue

				return {
					...item,
					remainingValue,
					remainingScalarValue,
					isValid: this.validateClaimBenefit(
						remainingScalarValue,
						item?.benefit?.typeId
					)
				}
			})
		},

		async fetchBenefitGraph() {
			const res = await api.benefitGraph({ where: { id: this.id } })
			this.benefitGraph =
				res?.data?.claims?.list?.[0]?.policy?.acceptedOffer?.product?.benefitGraph
		},

		async fetchFiles() {
			const res = await api.claimFiles({
				prefix: `claims/${this.id}/`
			})

			this.claimFiles =
				res?.data?.fileListing?.objectSummaries.map(item => ({
					fileName: item.key,
					path: item.key,
					key: item.key,
					created: item.lastModifiedAt
				})) || []
		},

		/**
		 * Method that will validate benefit remaining scalar value based on dictionary conditions
		 * If remaining value is less than 0 returns false, otherwise true
		 */
		validateClaimBenefit(remainingScalarValue, benefitId) {
			if (!benefitId || !remainingScalarValue) return true
			const remainingLimitKey = dictionary(benefitId)?.remainingLimit

			if (remainingLimitKey) {
				const remainingLimitValue = remainingScalarValue?.objectValue.find(
					item => item.key === remainingLimitKey
				)?.value?.numberValue

				if (remainingLimitValue < 0) return false
				return true
			}
		},

		async fetchPdfTemplates() {
			let variables = {}
			this.isLoading = true
			const res = await api.pdfTemplates({ variables })
			this.isLoading = false

			this.templates = res?.data?.templates?.list
				?.filter(
					item =>
						item?.__typename === "pdfDrawingTemplate" ||
						item?.__typename === "wkhtmltopdfTemplate"
				)
				?.map(item => ({
					name: item.name,
					value: item.id,
					typeName: item.__typename,
					graphQlQuery: item.graphQlQuery
				}))
		},

		afterRejectClaim() {
			this.$modal.hide("rejectClaimModal")
			this.fetchClaim()
		},

		canEditApprovedClaims: canEditApprovedClaims
	},

	created() {
		if (typeof this.id === "undefined") {
			this.$router.push({ name: "ClaimsList" })
			return
		}

		// We have separated fetching of claim data into a few groups for performance sake
		this.fetchClaim()
		this.fetchPdfTemplates()
		this.fetchClaimBenefitsAndBalance()
		this.fetchClaimSummary()
		this.fetchBenefitGraph()
		this.fetchFiles()
	}
}
</script>
